/**
 * Curated top-N job category ("field" facet) config for /jobs/category/{slug} pages.
 *
 * `field` is LLM-assigned free text (not an enum) — noisy, with near-duplicate spelling
 * variants. Each category lists every raw `field` value it should absorb via OR-filtering,
 * PER LOCALE: each Typesense collection stores `field` in its own language, so English-only
 * values match ~nothing on jobs_fi/jobs_sv (that bug shipped with phase 1 — the fi/sv
 * category pages served 2–6 jobs while en served 1,100+).
 *
 * fi/sv values were read from live Typesense facet counts per collection (Aug 2026) and
 * verified by cross-locale document joins on shared job ids where translations were
 * ambiguous. Typesense `:=` matches case-insensitively, so case-only variants are omitted.
 * Re-check periodically as the enrichment pipeline's field vocabulary drifts.
 */

import type { CategoryDefinition, SupportedLocale } from '../../types/category-hub';

export const CATEGORY_HUB_CONFIG: CategoryDefinition[] = [
  {
    slug: 'software-developer',
    fieldValues: {
      en: ['Software engineering', 'Software Developer'],
      fi: ['Ohjelmistokehitys', 'Ohjelmistotekniikka', 'Ohjelmistokehittäjä'],
      sv: ['Mjukvaruutveckling', 'Programvaruutveckling', 'Programvaruteknik', 'Mjukvaruteknik'],
    },
    displayNames: {
      en: 'Software Developer',
      fi: 'Ohjelmistokehittäjä',
      sv: 'Mjukvaruutvecklare',
    },
    relatedCategorySlugs: ['technology'],
  },
  {
    slug: 'sales',
    fieldValues: {
      en: ['Sales'],
      fi: ['Myynti'],
      sv: ['Försäljning'],
    },
    displayNames: {
      en: 'Sales',
      fi: 'Myynti',
      sv: 'Försäljning',
    },
    relatedCategorySlugs: ['sales-assistant'],
  },
  {
    slug: 'construction',
    fieldValues: {
      en: ['Construction'],
      fi: ['Rakentaminen', 'Rakennusala'],
      sv: ['Bygg och anläggning', 'Bygg'],
    },
    displayNames: {
      en: 'Construction',
      fi: 'Rakennusala',
      sv: 'Byggbranschen',
    },
    relatedCategorySlugs: ['house-builder', 'carpenter', 'electrician', 'civil-engineering-labourer'],
  },
  {
    slug: 'healthcare-assistant',
    fieldValues: {
      en: ['Healthcare Assistant', 'Health Care Assistants', 'Nurse Assistant'],
      fi: ['Lähihoitaja', 'Lähihoitajat', 'Kotihoidon Lähihoitaja', 'Hoiva-Avustaja'],
      sv: ['Undersköterska, Vård- Och Specialavdelning Och Mottagning', 'Undersköterska', 'Närvårdare'],
    },
    displayNames: {
      en: 'Healthcare Assistant',
      fi: 'Hoiva-avustaja',
      sv: 'Vårdassistent',
    },
    relatedCategorySlugs: ['healthcare', 'nurse'],
  },
  {
    slug: 'technology',
    fieldValues: {
      en: ['Technology'],
      fi: ['Teknologia'],
      sv: ['Teknologi'],
    },
    displayNames: {
      en: 'Technology',
      fi: 'Teknologia',
      sv: 'Teknik',
    },
    relatedCategorySlugs: ['software-developer'],
  },
  {
    slug: 'cook',
    fieldValues: {
      en: ['Cook'],
      fi: ['Kokki'],
      sv: ['Kokerska'],
    },
    displayNames: {
      en: 'Cook',
      fi: 'Kokki',
      sv: 'Kock',
    },
    relatedCategorySlugs: ['waiter-waitress'],
  },
  {
    slug: 'building-cleaner',
    fieldValues: {
      en: ['Building Cleaner'],
      fi: ['Laitossiivooja'],
      sv: ['Fastighetsstädare'],
    },
    displayNames: {
      en: 'Building Cleaner',
      fi: 'Kiinteistönhuoltaja',
      sv: 'Fastighetsstädare',
    },
    relatedCategorySlugs: ['construction'],
  },
  {
    slug: 'personal-assistant',
    fieldValues: {
      en: ['Personal Assistant'],
      fi: ['Henkilökohtainen Avustaja'],
      sv: ['Personlig Assistent'],
    },
    displayNames: {
      en: 'Personal Assistant',
      fi: 'Henkilökohtainen avustaja',
      sv: 'Personlig assistent',
    },
    relatedCategorySlugs: ['healthcare-assistant'],
  },
  {
    slug: 'welder',
    fieldValues: {
      en: ['Welder'],
      fi: ['Hitsaaja'],
      sv: ['Svetsare, Manuell'],
    },
    displayNames: {
      en: 'Welder',
      fi: 'Hitsaaja',
      sv: 'Svetsare',
    },
    relatedCategorySlugs: ['construction', 'electrician'],
  },
  {
    slug: 'sales-assistant',
    fieldValues: {
      en: ['Sales Assistant'],
      fi: ['Myyjä'],
      sv: ['Försäljningsassistent'],
    },
    displayNames: {
      en: 'Sales Assistant',
      fi: 'Myyjä',
      sv: 'Säljassistent',
    },
    relatedCategorySlugs: ['sales'],
  },
  {
    slug: 'healthcare',
    fieldValues: {
      en: ['Healthcare'],
      fi: ['Sosiaali- ja terveysala', 'Terveysala', 'Terveydenhuolto'],
      sv: ['Hälso- och sjukvård', 'Hälsovård'],
    },
    displayNames: {
      en: 'Healthcare',
      fi: 'Terveydenhuolto',
      sv: 'Hälsovård',
    },
    relatedCategorySlugs: ['healthcare-assistant', 'nurse'],
  },
  {
    slug: 'carpenter',
    fieldValues: {
      en: ['Carpenter', 'Carpenters And Joiners'],
      fi: ['Kirvesmies', 'Kirvesmiehet ja rakennuspuusepät'],
      sv: ['Snickare', 'Timmermän och byggnadssnickare'],
    },
    displayNames: {
      en: 'Carpenter',
      fi: 'Kirvesmies',
      sv: 'Snickare',
    },
    relatedCategorySlugs: ['construction', 'house-builder'],
  },
  {
    slug: 'house-builder',
    fieldValues: {
      en: ['House Builder', 'House Builders'],
      fi: ['Talonrakentaja', 'Talonrakentajat'],
      sv: ['Träarbetare', 'Byggnadsarbetare'],
    },
    displayNames: {
      en: 'House Builder',
      fi: 'Talonrakentaja',
      sv: 'Husbyggare',
    },
    relatedCategorySlugs: ['construction', 'carpenter'],
  },
  {
    slug: 'waiter-waitress',
    fieldValues: {
      en: ['Waiter/waitress', 'Waiters'],
      fi: ['Tarjoilija', 'Tarjoilijat'],
      sv: ['Servitör/servitris'],
    },
    displayNames: {
      en: 'Waiter/Waitress',
      fi: 'Tarjoilija',
      sv: 'Servitör/servitris',
    },
    relatedCategorySlugs: ['cook'],
  },
  {
    slug: 'building-construction-worker',
    fieldValues: {
      en: ['Building Construction Worker', 'Building Construction Labourers'],
      fi: ['Rakennustyömaan Avustava Työntekijä', 'Rakennusalan Avustavat Työntekijät'],
      sv: ['Medhjälpare, Byggnadsverksamhet'],
    },
    displayNames: {
      en: 'Building Construction Worker',
      fi: 'Rakennustyöntekijä',
      sv: 'Byggnadsarbetare',
    },
    relatedCategorySlugs: ['construction', 'civil-engineering-labourer'],
  },
  {
    slug: 'electrician',
    fieldValues: {
      en: ['Electrician'],
      fi: ['Sähköasentaja'],
      sv: ['Installationselektriker'],
    },
    displayNames: {
      en: 'Electrician',
      fi: 'Sähköasentaja',
      sv: 'Elektriker',
    },
    relatedCategorySlugs: ['construction', 'welder'],
  },
  {
    slug: 'civil-engineering-labourer',
    fieldValues: {
      en: ['Civil Engineering Labourers', 'Civil Engineering Worker'],
      fi: ['Maa- Ja Vesirakentamisen Avustavat Työntekijät', 'Maa- Ja Vesirakentamisen Avustava Työntekijä'],
      sv: ['Medhjälpare, Anläggningsverksamhet', 'Anläggningsarbetare'],
    },
    displayNames: {
      en: 'Civil Engineering Labourer',
      fi: 'Maanrakennustyöntekijä',
      sv: 'Anläggningsarbetare',
    },
    relatedCategorySlugs: ['construction', 'excavator-operator'],
  },
  {
    slug: 'excavator-operator',
    fieldValues: {
      en: ['Excavator Operator'],
      fi: ['Kaivinkoneenkuljettaja'],
      sv: ['Grävmaskinsförare'],
    },
    displayNames: {
      en: 'Excavator Operator',
      fi: 'Kaivinkoneenkuljettaja',
      sv: 'Grävmaskinist',
    },
    relatedCategorySlugs: ['civil-engineering-labourer', 'construction'],
  },
  {
    slug: 'nurse',
    fieldValues: {
      en: ['Nurse Responsible For General Care'],
      fi: ['Sairaanhoitaja'],
      sv: ['Sjuksköterska, Grundutbildad'],
    },
    displayNames: {
      en: 'Nurse',
      fi: 'Sairaanhoitaja',
      sv: 'Sjuksköterska',
    },
    relatedCategorySlugs: ['healthcare', 'healthcare-assistant'],
  },
  {
    slug: 'finance-accounting',
    fieldValues: {
      en: ['Finance & accounting'],
      fi: ['Talous ja kirjanpito'],
      sv: ['Ekonomi & redovisning', 'Ekonomi och redovisning'],
    },
    displayNames: {
      en: 'Finance & Accounting',
      fi: 'Talous ja kirjanpito',
      sv: 'Ekonomi och redovisning',
    },
    relatedCategorySlugs: ['sales', 'technology'],
  },
];

export function getAllCategorySlugs(): string[] {
  return CATEGORY_HUB_CONFIG.map((category) => category.slug);
}

export function getCategoryBySlug(slug: string): CategoryDefinition | null {
  if (!slug) return null;
  const normalized = slug.trim().toLowerCase();
  return CATEGORY_HUB_CONFIG.find((category) => category.slug.toLowerCase() === normalized) ?? null;
}

/**
 * Reverse lookup: raw `field` facet value -> owning category slug. Used to rebuild the
 * pretty `/jobs/category/{slug}` canonical URL once a `field` active filter is resolved.
 */
export function getCategorySlugByFieldValue(fieldValue: string): string | null {
  if (!fieldValue) return null;
  const normalized = fieldValue.trim().toLowerCase();
  const match = CATEGORY_HUB_CONFIG.find((category) =>
    (Object.values(category.fieldValues) as string[][]).some((values) =>
      values.some((value) => value.toLowerCase() === normalized),
    ),
  );
  return match?.slug ?? null;
}

/** Field facet values for `locale`, falling back to English. */
export function getCategoryFieldValuesForLocale(
  category: CategoryDefinition,
  locale: SupportedLocale,
): string[] {
  return category.fieldValues[locale] ?? category.fieldValues.en;
}

export function getCategoryDisplayName(slug: string, locale: SupportedLocale): string | null {
  return getCategoryBySlug(slug)?.displayNames[locale] ?? null;
}
